import { useAtomValue } from "jotai";
import type { PageProps } from "next";
import { useEffect } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import type { RelayComponent } from "relay-nextjs";
import { withRelay } from "relay-nextjs";
import { isString } from "scmp-sdk";

import { article, topic } from "shared/data";
import { notEmpty } from "shared/lib/utils";

import { FallbackLoading } from "scmp-app/components/fallback-loading";
import { PlusMobileScreen } from "scmp-app/components/plus/mobile-screen";
import { PlusHomeSeo } from "scmp-app/components/plus/plus-home-seo";
import { trackingAtom } from "scmp-app/components/tracking";
import { createClientEnvironment } from "scmp-app/lib/relay/environment.client";
import { useGetPageLocationForPlusPage } from "scmp-app/pages/plus/hooks";
import { plusGlobalCss } from "scmp-app/pages/plus/styles";
import type { archiveInPlusPageQuery } from "scmp-app/queries/__generated__/archiveInPlusPageQuery.graphql";

import { StyledPlusArchive } from "./styles";

export const query = graphql`
  query archiveInPlusPageQuery(
    $nodeId: ID!
    $hasNodeId: Boolean!
    $dailyPulseTypeUuid: String!
    $factSheetTypeUuid: String!
    $quickDigestTypeUuid: String!
    $openQuestionsSeriesTypeUuid: String!
    $chinaFutureTechBriefingsTypeUuid: String!
    $betweenTheLinesTypeUuid: String!
  ) {
    node(id: $nodeId) @include(if: $hasNodeId) {
      ...plusHomeSeoNode
    }
    ...archivePlusArchiveQuery
      @arguments(
        chinaFutureTechBriefingsTypeUuid: $chinaFutureTechBriefingsTypeUuid
        dailyPulseTypeUuid: $dailyPulseTypeUuid
        factSheetTypeUuid: $factSheetTypeUuid
        quickDigestTypeUuid: $quickDigestTypeUuid
        openQuestionsSeriesTypeUuid: $openQuestionsSeriesTypeUuid
        betweenTheLinesTypeUuid: $betweenTheLinesTypeUuid
      )
  }
`;

const PlusArchivePage: RelayComponent<PageProps, archiveInPlusPageQuery> = ({ preloadedQuery }) => {
  const data = usePreloadedQuery(query, preloadedQuery);
  const pageLocation = useGetPageLocationForPlusPage();
  const { trackPageView } = useAtomValue(trackingAtom);
  useEffect(() => {
    if (!notEmpty(pageLocation)) {
      return;
    }
    trackPageView?.({
      page_location: pageLocation,
    });
  }, [pageLocation, trackPageView]);
  return (
    <>
      <PlusHomeSeo reference={data.node} />
      <PlusMobileScreen />
      <StyledPlusArchive reference={data} />
    </>
  );
};

PlusArchivePage.displayName = "PlusArchivePage";

const pageProps: PageProps = {
  animateKey: "plus-archive",
  appBarConfiguration: {
    variant: "scmp/plus",
  },
  appInitializerConfiguration: {
    application: "plus",
  },
  extraGlobalCss: plusGlobalCss,
};

export default withRelay(PlusArchivePage, query, {
  clientSideProps: () => pageProps,
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("scmp-app/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: () => Promise.resolve(pageProps),
  variablesFromContext(context) {
    return {
      betweenTheLinesTypeUuid: article.types.betweenTheLines.entityUuid,
      chinaFutureTechBriefingsTypeUuid: article.types.chinaFutureTechBriefing.entityUuid,
      dailyPulseTypeUuid: article.types.dailyPulseBriefing.entityUuid,
      factSheetTypeUuid: article.types.factSheet.entityUuid,
      hasNodeId: notEmpty(context.query?.nodeId),
      nodeId: isString(context.query.nodeId) ? (context.query?.nodeId ?? "") : "",
      openQuestionsSeriesTypeUuid: topic.openQuestionsSeries.entityUuid,
      quickDigestTypeUuid: article.types.quickDigest.entityUuid,
    };
  },
});
